import {
  ScreenHeaderProvider,
  ScreenHeaderBorder,
  ScreenHeaderContent,
  ScreenHeaderMain,
  ScreenHeaderPersistentTopBar,
  ScreenHeaderPersistentTopBarLayout,
  ScreenHeaderPersistentTopBarTitle,
  ScreenHeaderTitle,
} from '@/components/ScreenHeader'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import type { ReactNode } from 'react'
import { useObservableValue } from '@/hooks/rx-hooks'
import styles from './PageHeader.module.css'
import { Button } from '@/components/Button'
import { mediaQueryMatchStream } from '@/utils/browser-util'
import { IconButton } from '@/components/IconButton'

interface PageHeaderProps {
  action?: ReactNode
  backButton?: ReactNode
  title?: string
  children?: ReactNode
  testId?: string
}

/**
 * The PageHeader
 */
export function PageHeader({
  action,
  backButton,
  title,
  children,
  testId,
}: PageHeaderProps) {
  const isSpacious = useIsSpacious()
  const isTitleVisible = !backButton
  return (
    <ScreenHeaderProvider isOverlay applyDocumentScrollPadding>
      <ScreenHeaderPersistentTopBar fadeWhen="title-exit">
        <ScreenHeaderPersistentTopBarLayout
          visibility={!isSpacious ? 'content-exit' : 'content-exit'}
          left={backButton}
          right={action}
        >
          <ScreenHeaderPersistentTopBarTitle visibility="content-exit">
            {isTitleVisible && title}
          </ScreenHeaderPersistentTopBarTitle>
        </ScreenHeaderPersistentTopBarLayout>
      </ScreenHeaderPersistentTopBar>

      <ScreenHeaderMain>
        <ScreenHeaderContent>
          <header className={styles.header} data-test={testId}>
            <div className={styles.headerContent}>
              <ScreenHeaderTitle>
                <div className="flex items-center">
                  {backButton && <div className="flex-none">{backButton}</div>}

                  <div className="flex-auto">
                    {isTitleVisible && <p className={styles.title}>{title}</p>}
                  </div>

                  <div className="flex-none">{action}</div>
                </div>
              </ScreenHeaderTitle>
            </div>
          </header>
        </ScreenHeaderContent>
        <ScreenHeaderBorder />
      </ScreenHeaderMain>
      {children}
    </ScreenHeaderProvider>
  )
}

export interface StartNewServiceButtonProps {
  isSkeleton?: boolean
  to?: string
  onPress?: () => unknown
  isDisabled?: boolean
  isPending?: boolean
  label?: string
  compactLabel?: string
  testId?: string
}

/**
 * Start new service button.
 */
export function StartNewServiceButton({
  isSkeleton,
  to,
  onPress,
  isPending,
  isDisabled,
  label = 'Start new service',
  compactLabel = 'Start new',
  testId,
}: StartNewServiceButtonProps) {
  const isSpacious = useIsSpacious()

  return !isSpacious ? (
    <Button
      isSkeleton={isSkeleton}
      isPending={isPending}
      isDisabled={isDisabled}
      iconLeft="add"
      label={compactLabel ?? label}
      to={to}
      onPress={onPress}
      preventScrollReset
      testId={testId}
    />
  ) : (
    <Button
      isSkeleton={isSkeleton}
      isPending={isPending}
      isDisabled={isDisabled}
      label={label}
      to={to}
      onPress={onPress}
      preventScrollReset
      testId={testId}
    />
  )
}

export interface ReturnButtonProps {
  label: string
  href?: string
  to?: string
  onPress?: () => unknown
  testId?: string
}

const isShowingFullButtonMediaQuery$ = mediaQueryMatchStream(
  'screen and (min-width: 500px)',
)

/**
 * Return button, for B2B2C integrators.
 */
export function ReturnButton({ label, testId, ...rest }: ReturnButtonProps) {
  const isShowingFullButton = useObservableValue(isShowingFullButtonMediaQuery$)

  if (isShowingFullButton) {
    return (
      <Button
        variant="outline"
        iconLeft="back"
        label={label}
        testId={testId}
        {...rest}
      />
    )
  }

  return <IconButton name="back" label={label} testId={testId} {...rest} />
}
